import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { userAPI, agentAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import { Shield, Zap, Terminal, Activity, CreditCard, ArrowRight, Play, Clock, TrendingUp, Crown } from 'lucide-react';
import { motion } from 'framer-motion';

const FOUNDER_EMAIL = 'info@defences.ai';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [dashRes, agentsRes] = await Promise.all([
          userAPI.dashboard(),
          agentAPI.list()
        ]);
        setDashboard(dashRes.data);
        setAgents(agentsRes.data.agents.filter(a => a.is_active));
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020202]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="w-8 h-8 border-2 border-[#FF7A3D] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const credits = dashboard?.credits ?? user?.credits ?? 0;
  const totalExec = dashboard?.total_executions ?? 0;
  const recentExec = dashboard?.recent_executions ?? [];
  const agentUsage = dashboard?.agent_usage ?? [];

  return (
    <div className="min-h-screen bg-[#020202] grid-bg" data-testid="dashboard-page">
      <Navbar />
      <div className="pt-20 px-6 md:px-12 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-10">
            <p className="font-mono text-xs text-[#FF7A3D] tracking-widest uppercase mb-2">COMMAND CENTER</p>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">
              Welcome back, <span className="text-[#FF7A3D]">{user?.name || 'Operative'}</span>
            </h1>
          </div>

          {/* Privileged quick access (founder / admin) */}
          {(user?.is_admin || (user?.email || '').toLowerCase() === FOUNDER_EMAIL) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-testid="privileged-access-row">
              {(user?.email || '').toLowerCase() === FOUNDER_EMAIL && (
                <button
                  onClick={() => navigate('/founder')}
                  className="group relative overflow-hidden rounded-sm p-5 text-left border border-[#FF7A3D]/30 bg-[#0a0c11]/70 hover:border-[#FF7A3D]/60 transition-colors"
                  data-testid="dashboard-cockpit-card"
                >
                  <div className="absolute inset-0 opacity-60 pointer-events-none" style={{ background: 'radial-gradient(circle at 10% 20%, rgba(255,122,61,0.12), transparent 55%), radial-gradient(circle at 90% 80%, rgba(0,229,193,0.10), transparent 55%)' }} />
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Crown className="w-5 h-5 text-[#FF7A3D]" />
                      <div>
                        <p className="font-heading text-sm font-bold text-white">FOUNDER COCKPIT</p>
                        <p className="font-mono text-[11px] text-[#94a3b8]">$10M PAT countdown · live MRR/ARR · war room</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#FF7A3D] group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              )}
              {user?.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="group relative overflow-hidden rounded-sm p-5 text-left border border-[#00E5C1]/25 bg-[#0a0c11]/70 hover:border-[#00E5C1]/50 transition-colors"
                  data-testid="dashboard-admin-card"
                >
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-[#00E5C1]" />
                      <div>
                        <p className="font-heading text-sm font-bold text-white">ADMIN SUITE</p>
                        <p className="font-mono text-[11px] text-[#94a3b8]">users · agents · payments · settings</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#00E5C1] group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              )}
            </div>
          )}

          {/* Stats Grid - Bento */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="card-cyber p-6 rounded-sm"
              data-testid="credits-card"
            >
              <div className="flex items-center justify-between mb-3">
                <CreditCard className="w-5 h-5 text-[#FF7A3D]" />
                <Badge className="bg-[#FF7A3D]/10 text-[#FF7A3D] border-[#FF7A3D]/20 font-mono text-xs">BALANCE</Badge>
              </div>
              <p className="font-heading text-3xl font-bold text-white">{credits.toFixed(2)}</p>
              <p className="font-mono text-xs text-[#999] mt-1">CREDITS AVAILABLE</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card-cyber p-6 rounded-sm"
              data-testid="executions-card"
            >
              <div className="flex items-center justify-between mb-3">
                <Terminal className="w-5 h-5 text-[#00E5C1]" />
                <Badge className="bg-[#00E5C1]/10 text-[#00E5C1] border-[#00E5C1]/20 font-mono text-xs">TOTAL</Badge>
              </div>
              <p className="font-heading text-3xl font-bold text-white">{totalExec}</p>
              <p className="font-mono text-xs text-[#999] mt-1">AGENT EXECUTIONS</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="card-cyber p-6 rounded-sm"
              data-testid="agents-card"
            >
              <div className="flex items-center justify-between mb-3">
                <Shield className="w-5 h-5 text-[#FF7A3D]" />
                <Badge className="bg-[#FF7A3D]/10 text-[#FF7A3D] border-[#FF7A3D]/20 font-mono text-xs">ACTIVE</Badge>
              </div>
              <p className="font-heading text-3xl font-bold text-white">{agents.length}</p>
              <p className="font-mono text-xs text-[#999] mt-1">AGENTS DEPLOYED</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card-cyber p-6 rounded-sm"
              data-testid="plan-card"
            >
              <div className="flex items-center justify-between mb-3">
                <Zap className="w-5 h-5 text-[#ffbd2e]" />
                <Badge className="bg-[#ffbd2e]/10 text-[#ffbd2e] border-[#ffbd2e]/20 font-mono text-xs">PLAN</Badge>
              </div>
              <p className="font-heading text-3xl font-bold text-white uppercase">{user?.plan || 'Free'}</p>
              <p className="font-mono text-xs text-[#999] mt-1">CURRENT TIER</p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Quick Launch */}
            <div className="lg:col-span-2 card-cyber p-6 rounded-sm" data-testid="quick-launch">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-lg font-bold text-white">QUICK LAUNCH</h2>
                <Link to="/marketplace" className="font-mono text-xs text-[#FF7A3D] hover:underline">VIEW ALL</Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {agents.slice(0, 4).map(agent => (
                  <button
                    key={agent.agent_id}
                    onClick={() => navigate(`/agents/${agent.agent_id}/console`)}
                    className="flex items-center gap-3 p-4 bg-white/[0.02] border border-white/5 rounded-sm hover:border-[#FF7A3D]/30 hover:bg-[#FF7A3D]/[0.02] transition-all text-left group"
                    data-testid={`quick-launch-${agent.agent_id}`}
                  >
                    <Play className="w-4 h-4 text-[#aaa] group-hover:text-[#FF7A3D] transition-colors flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-heading text-sm font-bold text-white truncate">{agent.name}</p>
                      <p className="font-mono text-xs text-[#aaa]">{agent.price_per_run} credits/run</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Agent Usage */}
            <div className="card-cyber p-6 rounded-sm" data-testid="agent-usage">
              <h2 className="font-heading text-lg font-bold text-white mb-6">USAGE BREAKDOWN</h2>
              {agentUsage.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="w-8 h-8 text-[#aaa] mx-auto mb-3" />
                  <p className="font-mono text-xs text-[#aaa]">No usage data yet</p>
                  <Button
                    onClick={() => navigate('/marketplace')}
                    className="mt-4 bg-[#FF7A3D] text-black font-heading text-xs uppercase rounded-sm h-8"
                    data-testid="start-scanning-btn"
                  >
                    START SCANNING
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {agentUsage.slice(0, 5).map(u => (
                    <div key={u.agent_id} className="flex items-center justify-between">
                      <span className="font-mono text-xs text-[#888] truncate mr-2">{u.agent_id}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-[#FF7A3D]">{u.count}x</span>
                        <span className="font-mono text-xs text-[#aaa]">{u.credits.toFixed(2)}cr</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Executions */}
          <div className="mt-4 card-cyber p-6 rounded-sm" data-testid="recent-executions">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg font-bold text-white">RECENT ACTIVITY</h2>
              <Clock className="w-4 h-4 text-[#aaa]" />
            </div>
            {recentExec.length === 0 ? (
              <div className="text-center py-12">
                <Terminal className="w-10 h-10 text-[#555] mx-auto mb-4" />
                <p className="font-body text-[#aaa] mb-2">No executions yet</p>
                <p className="font-mono text-xs text-[#888]">Run your first agent to see activity here</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentExec.slice(0, 5).map(exec => (
                  <div key={exec.exec_id} className="flex items-center gap-4 p-3 bg-white/[0.01] border border-white/5 rounded-sm">
                    <div className="w-2 h-2 rounded-full bg-[#FF7A3D]" />
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xs text-white truncate">{exec.agent_id}</p>
                      <p className="font-mono text-xs text-[#aaa] truncate">{exec.input_text?.substring(0, 60)}...</p>
                    </div>
                    <span className="font-mono text-xs text-[#FF7A3D] flex-shrink-0">-{exec.credits_used}cr</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bottom CTA */}
          {credits < 5 && (
            <div className="mt-4 card-cyber p-6 rounded-sm border-[#ffbd2e]/20 flex items-center justify-between" data-testid="low-credits-banner">
              <div>
                <p className="font-heading text-sm font-bold text-white">LOW CREDITS</p>
                <p className="font-mono text-xs text-[#999]">Top up to continue using agents</p>
              </div>
              <Button onClick={() => navigate('/pricing')} className="bg-[#FF7A3D] text-black font-heading text-xs uppercase rounded-sm" data-testid="buy-credits-btn">
                BUY CREDITS <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
